import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderAPI } from '../services/api';
import { Order } from '../types';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';
import './OrderConfirmation.css';

const OrderConfirmation: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const data = await orderAPI.trackOrder(orderNumber!);
        setOrder(data);

        // Track Purchase if not already tracked for this order
        const trackedOrders = JSON.parse(localStorage.getItem('trackedOrders') || '[]');
        if (data && !trackedOrders.includes(data.order_number)) {
          // Extract product IDs - handle different API response formats
          const contentIds = data.items.map((item: any) => {
            // Try different possible field names for product ID
            const productId = item.product_id || item.product || item.id;
            return productId?.toString() || '';
          }).filter(Boolean);

          // Calculate total value - ensure it's a number
          const totalValue = parseFloat(String(data.final_amount || data.total_amount || 0));

          // Calculate total items
          const numItems = data.items.reduce((acc: number, item: any) => acc + (item.quantity || 1), 0);

          // Track Facebook & TikTok Purchase event
          await trackFacebookEvent('Purchase', {
            content_id: contentIds[0] || '',
            content_ids: contentIds,
            content_type: 'product',
            value: totalValue,
            currency: 'BDT',
            num_items: numItems,
            order_id: data.order_number
          });

          // Track GTM/GA4 Purchase event
          gtmEvents.purchase({
            orderId: data.order_number || '',
            totalValue: totalValue,
            shipping: 0, // Shipping cost not tracked separately in Order
            items: data.items.map((item: any) => ({
              id: item.product_id || item.product || item.id,
              name: item.product_name || item.name || '',
              price: parseFloat(String(item.price || 0)),
              quantity: item.quantity || 1,
            })),
          });

          console.log('Purchase events tracked (FB + GTM):', {
            order_id: data.order_number,
            value: totalValue,
            content_ids: contentIds,
            num_items: numItems
          });

          // Mark as tracked only after successful tracking
          trackedOrders.push(data.order_number);
          localStorage.setItem('trackedOrders', JSON.stringify(trackedOrders));
        }
      } catch (error) {
        console.error('Failed to load order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (orderNumber) {
      loadOrder();
    }
  }, [orderNumber]);

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  if (!order) {
    return <div className="error-container"><p>{t('common.error')}</p></div>;
  }

  return (
    <div className="order-confirmation-page">
      <div className="container">
        <div className="confirmation-header">
          <div className="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1>{t('orderConfirmation.title')}</h1>
          <p className="success-message">আনুমানিক ডেলিভারি: ৩-৫ কার্যদিবস</p>
        </div>

        <div className="order-summary-grid">
          {/* Order Number Card */}
          <div className="info-card primary-card">
            <div className="card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            </div>
            <div className="card-content">
              <h3>{t('orderConfirmation.orderNumber')}</h3>
              <p className="order-number">{order.order_number}</p>
            </div>
          </div>

          {/* Order Date Card */}
          <div className="info-card">
            <div className="card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div className="card-content">
              <h3>{t('orderConfirmation.orderDate')}</h3>
              <p>{new Date(order.created_at!).toLocaleDateString('bn-BD', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>
          </div>

          {/* Payment Method Card */}
          <div className="info-card">
            <div className="card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            </div>
            <div className="card-content">
              <h3>Payment Method</h3>
              <p>{t('checkout.cashOnDelivery')}</p>
            </div>
          </div>

          {/* Total Amount Card */}
          <div className="info-card highlight-card">
            <div className="card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="card-content">
              <h3>{t('orderConfirmation.totalAmount')}</h3>
              <p className="total-amount">৳{Number(order.total_amount || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Order Items Section */}
        <div className="order-items-section">
          <h3 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {t('orderConfirmation.orderItems')}
          </h3>
          <div className="items-list">
            {order.items.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-main">
                  {item.product_image && (
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="item-image"
                    />
                  )}
                  <div className="item-details-wrapper">
                    <div className="item-name">{item.product_name}</div>
                    <div className="item-meta">
                      <span className="item-quantity">Qty: {item.quantity}</span>
                      <span className="item-separator">•</span>
                      <span className="item-unit-price">৳{Number(item.price || 0).toFixed(2)} each</span>
                    </div>
                  </div>
                </div>
                <div className="item-subtotal">
                  <span className="subtotal-amount">৳{Number(item.subtotal || 0).toFixed(2)}</span>
                </div>
              </div>
            ))}

            {/* Order Summary */}
            <div className="order-total-breakdown">
              <div className="breakdown-row">
                <span>Subtotal</span>
                <span>৳{(Number(order.total_amount || 0) - Number(order.shipping_cost || 0)).toFixed(2)}</span>
              </div>
              <div className="breakdown-row">
                <span>Shipping Fee</span>
                <span>৳{Number(order.shipping_cost || 0).toFixed(2)}</span>
              </div>
              <div className="breakdown-divider"></div>
              <div className="breakdown-row total">
                <span>মোট পরিমাণ</span>
                <span>৳{Number(order.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Shipping Info */}
        <div className="info-sections-grid">
          <div className="shipping-info-section">
            <h3 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {t('orderConfirmation.shippingAddress')}
            </h3>
            <div className="info-content">
              <p className="address-text">{order.shipping_address}</p>
            </div>
          </div>

          {order.customer_phone && (
            <div className="customer-info-section">
              <h3 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Contact Information
              </h3>
              <div className="info-content">
                <p><strong>Name:</strong> {order.customer_name}</p>
                <p><strong>Phone:</strong> {order.customer_phone}</p>
              </div>
            </div>
          )}

          {order.notes && (
            <div className="notes-section">
              <h3 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Order Notes
              </h3>
              <div className="info-content">
                <p className="notes-text">{order.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="confirmation-actions">
          <Link to="/products" className="btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            {t('cart.continueShopping')}
          </Link>
          {/* <Link to={`/track-order?order=${order.order_number}`} className="btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <polyline points="17 11 19 13 23 9" />
            </svg>
            {t('orderConfirmation.trackOrder')}
          </Link> */}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
